import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  createTraceShare,
  deleteTraceShare,
  getRunTrace,
  getRunTraceSummary,
  getSharedTraceByToken,
  type RunTrace,
  type RunTraceSummary,
  type ShareInfo,
} from "../lib/api";
import { StatusBadge, formatDuration, formatRelativeTime } from "./TracesPage";

/**
 * Trace viewer — renders a single `/api/runs/:id/trace` manifest.
 *
 * The page is organised as a set of panels selectable by tab:
 *   Timeline     — tests in chronological order (this task, #107)
 *   Screenshots  — the screenshots[] gallery (this task, #107)
 *   DOM          — DOM snapshot diff viewer (task #108)
 *   Network      — request waterfall (task #109)
 *
 * Logs (stdout/stderr) live in a collapsible section at the bottom of the
 * page regardless of which tab is active, so they're always a click away.
 *
 * We expand `manifest.tests` into a derived array here — the manifest JSON
 * is authoritative but it comes in as `Record<string, unknown>`, so each
 * panel does its own narrowing. That keeps the API client free of a
 * hard dep on @uaip/trace.
 */

type Tab = "timeline" | "screenshots" | "dom" | "network" | "triage";

interface ViewTest {
  name: string;
  className?: string;
  status: "passed" | "failed" | "skipped" | "errored" | string;
  durationMs: number;
  errorMessage?: string;
  errorStack?: string;
}

interface ViewArtifact {
  stepId?: string;
  testName?: string;
  ts?: string;
  path: string;
  width?: number;
  height?: number;
  bytes?: number;
  /**
   * Optional inline HTML for DOM snapshots. Traces written before v1.1 won't
   * have this; panels should fall back to the `path` reference and prompt the
   * user to wire the artifact route.
   */
  htmlInline?: string;
}

interface ViewNetworkEvent {
  ts?: string;
  method: string;
  url: string;
  status: number;
  durationMs: number;
  requestBytes?: number;
  responseBytes?: number;
  fromTest?: string;
}

/**
 * Mirror of @uaip/trace's TraceTriage shape, narrowed client-side so the
 * api client can keep treating manifests as opaque JSON. All fields are
 * already string/number/null so nothing exotic to coerce.
 */
interface ViewTriage {
  testName: string;
  failureSignature: string;
  completedAt: string;
  durationMs: number;
  category:
    | "selector_drift"
    | "app_bug"
    | "timing"
    | "flaky"
    | "environment"
    | string;
  confidence: number;
  rootCauseHypothesis: string;
  proposedAction:
    | "retry"
    | "repair_selector"
    | "file_bug"
    | "skip_flake"
    | "investigate"
    | string;
  evidence: string[];
  selfHealSuggestion: {
    oldSelector: string;
    newSelectorBundle: string[];
    rationale: string;
    confidence: number;
  } | null;
  model: string;
  provider: string;
}

/**
 * Two viewer modes share this page:
 *   - "authed": URL `/runs/:id/trace` — owner reads via /api/runs/:id/trace
 *     and can mint/rotate/revoke share tokens.
 *   - "public": URL `/public/traces/:token` — anonymous read via
 *     /api/public/traces/:token; share controls hidden.
 *
 * The render body (panels, tabs, logs) is identical between the two — only
 * the data loader and the header CTA differ. We co-locate them rather than
 * forking the page so the two viewers can't drift visually.
 */
type ViewerMode = "authed" | "public";

export function TraceViewerPage(props: { mode?: ViewerMode } = {}): JSX.Element {
  const mode: ViewerMode = props.mode ?? "authed";
  const params = useParams();
  const id = mode === "authed" ? (params.id ?? "") : "";
  const token = mode === "public" ? (params.token ?? "") : "";

  const [trace, setTrace] = useState<RunTrace | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("timeline");
  const [summary, setSummary] = useState<RunTraceSummary | null>(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setTrace(null);
    setError(null);
    (async () => {
      try {
        if (mode === "public") {
          const t = await getSharedTraceByToken(token);
          // Adapt SharedRunTrace → RunTrace (shape differs only in user_id
          // which the panels never read; we synthesise an empty userId).
          if (!cancelled) {
            setTrace({
              runId: t.runId,
              userId: "",
              uploadedAt: t.uploadedAt,
              status: t.status,
              framework: t.framework,
              runner: t.runner,
              durationMs: t.durationMs,
              testCount: t.testCount,
              manifest: t.manifest,
            });
          }
        } else {
          const t = await getRunTrace(id);
          if (!cancelled) setTrace(t);
          // Fetch the summary too so we know whether the trace is currently
          // shared (for the Share button label/styling).
          try {
            const s = await getRunTraceSummary(id);
            if (!cancelled) setSummary(s);
          } catch {
            // Non-fatal — Share button will fall back to "Not shared".
          }
        }
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode, id, token]);

  const tests = useMemo<ViewTest[]>(() => {
    const raw = trace?.manifest.tests;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((t): t is Record<string, unknown> => !!t && typeof t === "object")
      .map((t) => {
        // `exactOptionalPropertyTypes` in the web tsconfig forbids assigning
        // `undefined` to an optional property — we have to spread the field
        // in only when it's a real value.
        const out: ViewTest = {
          name: typeof t.name === "string" ? t.name : "<unnamed>",
          status: typeof t.status === "string" ? t.status : "unknown",
          durationMs:
            typeof t.durationMs === "number" && Number.isFinite(t.durationMs)
              ? t.durationMs
              : 0,
        };
        if (typeof t.className === "string") out.className = t.className;
        if (typeof t.errorMessage === "string") out.errorMessage = t.errorMessage;
        if (typeof t.errorStack === "string") out.errorStack = t.errorStack;
        return out;
      });
  }, [trace]);

  const networkEvents = useMemo<ViewNetworkEvent[]>(() => {
    const raw = trace?.manifest.networkRequests;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((n): n is Record<string, unknown> => !!n && typeof n === "object")
      .map((n): ViewNetworkEvent => {
        const out: ViewNetworkEvent = {
          method: typeof n.method === "string" ? n.method : "GET",
          url: typeof n.url === "string" ? n.url : "",
          status:
            typeof n.status === "number" && Number.isFinite(n.status)
              ? n.status
              : -1,
          durationMs:
            typeof n.durationMs === "number" && Number.isFinite(n.durationMs)
              ? n.durationMs
              : 0,
        };
        if (typeof n.ts === "string") out.ts = n.ts;
        if (typeof n.requestBytes === "number") out.requestBytes = n.requestBytes;
        if (typeof n.responseBytes === "number")
          out.responseBytes = n.responseBytes;
        if (typeof n.fromTest === "string") out.fromTest = n.fromTest;
        return out;
      })
      .filter((n) => n.url.length > 0);
  }, [trace]);

  const domSnapshots = useMemo<ViewArtifact[]>(() => {
    const raw = trace?.manifest.domSnapshots;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((a): a is Record<string, unknown> => !!a && typeof a === "object")
      .map((a): ViewArtifact => {
        const out: ViewArtifact = {
          path: typeof a.path === "string" ? a.path : "",
        };
        if (typeof a.stepId === "string") out.stepId = a.stepId;
        if (typeof a.testName === "string") out.testName = a.testName;
        if (typeof a.ts === "string") out.ts = a.ts;
        if (typeof a.bytes === "number") out.bytes = a.bytes;
        // DOM snapshots may also carry an inline HTML blob when the writer
        // chose to inline small documents. `htmlInline` is a v1.1 addition —
        // ignored gracefully if missing.
        if (typeof a.htmlInline === "string") out.htmlInline = a.htmlInline;
        return out;
      })
      .filter((a) => a.path.length > 0);
  }, [trace]);

  const screenshots = useMemo<ViewArtifact[]>(() => {
    const raw = trace?.manifest.screenshots;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((a): a is Record<string, unknown> => !!a && typeof a === "object")
      .map((a): ViewArtifact => {
        const out: ViewArtifact = {
          path: typeof a.path === "string" ? a.path : "",
        };
        if (typeof a.stepId === "string") out.stepId = a.stepId;
        if (typeof a.testName === "string") out.testName = a.testName;
        if (typeof a.ts === "string") out.ts = a.ts;
        if (typeof a.width === "number") out.width = a.width;
        if (typeof a.height === "number") out.height = a.height;
        if (typeof a.bytes === "number") out.bytes = a.bytes;
        return out;
      })
      .filter((a) => a.path.length > 0);
  }, [trace]);

  const triages = useMemo<ViewTriage[]>(() => {
    const raw = (trace?.manifest as Record<string, unknown> | undefined)?.triages;
    if (!Array.isArray(raw)) return [];
    return raw
      .filter((t): t is Record<string, unknown> => !!t && typeof t === "object")
      .map((t): ViewTriage | null => {
        const evidenceRaw = t.evidence;
        const evidence: string[] = Array.isArray(evidenceRaw)
          ? evidenceRaw.filter((e): e is string => typeof e === "string")
          : [];
        const shealRaw = t.selfHealSuggestion;
        let selfHeal: ViewTriage["selfHealSuggestion"] = null;
        if (shealRaw && typeof shealRaw === "object") {
          const s = shealRaw as Record<string, unknown>;
          const bundle = Array.isArray(s.newSelectorBundle)
            ? s.newSelectorBundle.filter((x): x is string => typeof x === "string")
            : [];
          if (
            typeof s.oldSelector === "string" &&
            typeof s.rationale === "string" &&
            typeof s.confidence === "number" &&
            bundle.length > 0
          ) {
            selfHeal = {
              oldSelector: s.oldSelector,
              rationale: s.rationale,
              confidence: s.confidence,
              newSelectorBundle: bundle,
            };
          }
        }
        const testName = typeof t.testName === "string" ? t.testName : "";
        const category = typeof t.category === "string" ? t.category : "investigate";
        const proposedAction =
          typeof t.proposedAction === "string" ? t.proposedAction : "investigate";
        const hypothesis =
          typeof t.rootCauseHypothesis === "string" ? t.rootCauseHypothesis : "";
        const confidence =
          typeof t.confidence === "number" && Number.isFinite(t.confidence)
            ? t.confidence
            : 0;
        if (!testName || !hypothesis) return null;
        return {
          testName,
          failureSignature:
            typeof t.failureSignature === "string" ? t.failureSignature : "",
          completedAt: typeof t.completedAt === "string" ? t.completedAt : "",
          durationMs:
            typeof t.durationMs === "number" && Number.isFinite(t.durationMs)
              ? t.durationMs
              : 0,
          category,
          confidence,
          rootCauseHypothesis: hypothesis,
          proposedAction,
          evidence,
          selfHealSuggestion: selfHeal,
          model: typeof t.model === "string" ? t.model : "",
          provider: typeof t.provider === "string" ? t.provider : "",
        };
      })
      .filter((t): t is ViewTriage => t !== null);
  }, [trace]);

  const stdoutInline = typeof trace?.manifest.stdoutInline === "string"
    ? (trace!.manifest.stdoutInline as string)
    : "";
  const stderrInline = typeof trace?.manifest.stderrInline === "string"
    ? (trace!.manifest.stderrInline as string)
    : "";

  // Identifier shown in the header — run id (authed) or "shared" + run id
  // (public, once the trace has loaded).
  const displayId = mode === "authed" ? id : trace?.runId ?? "";

  return (
    <div>
      <header className="mb-6">
        {mode === "authed" ? (
          <div className="mb-1 text-xs text-slate-500">
            <Link to="/traces" className="hover:text-slate-300">
              ← All traces
            </Link>
          </div>
        ) : (
          // Public mode: no "All traces" link (the viewer isn't signed in).
          // Show a small attribution badge instead so the visitor knows what
          // they're looking at and where it came from.
          <div className="mb-1 inline-flex items-center gap-2 text-xs text-slate-500">
            <span className="rounded-full border border-slate-700 bg-slate-900 px-2 py-0.5">
              shared trace · read-only
            </span>
          </div>
        )}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">
              Trace <span className="font-mono text-base text-slate-400">{displayId}</span>
            </h1>
            {trace && (
              <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-400">
                {/* "passed but 0 tests" is silently green by default — the
                 *  trace writer's deriveStatus() returns "passed" whenever
                 *  exitCode=0 and no failed tests, but tests=[] satisfies
                 *  that too. Render an honest amber chip instead so the
                 *  user notices the suite ran zero things. */}
                {trace.status === "passed" && tests.length === 0 ? (
                  <span
                    className="inline-flex items-center rounded-md border border-amber-600 bg-amber-500/15 px-2 py-0.5 text-xs text-amber-200"
                    title="The runner exited 0 but no JUnit reports were found."
                  >
                    passed · 0 tests
                  </span>
                ) : (
                  <StatusBadge status={trace.status} />
                )}
                <span>{trace.framework}</span>
                <span className="text-slate-600">·</span>
                <span>{trace.runner}</span>
                <span className="text-slate-600">·</span>
                <span>{trace.testCount} tests</span>
                <span className="text-slate-600">·</span>
                <span>{formatDuration(trace.durationMs)}</span>
                <span className="text-slate-600">·</span>
                <span>uploaded {formatRelativeTime(trace.uploadedAt)}</span>
              </div>
            )}
          </div>
          {mode === "authed" && trace && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShareOpen(true)}
                className={
                  summary?.isShared
                    ? "inline-flex items-center gap-1.5 rounded-md border border-emerald-700 bg-emerald-900/30 px-3 py-1.5 text-sm text-emerald-200 hover:bg-emerald-900/50"
                    : "inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-900 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-800"
                }
              >
                {summary?.isShared ? "● Shared — manage link" : "Share link"}
              </button>
            </div>
          )}
        </div>
      </header>

      {mode === "authed" && shareOpen && (
        <ShareDialog
          runId={id}
          alreadyShared={summary?.isShared ?? false}
          onClose={() => setShareOpen(false)}
          onChange={(s) =>
            setSummary((prev) =>
              prev ? { ...prev, isShared: s } : prev,
            )
          }
        />
      )}

      {error && (
        <div className="mb-4 rounded-md border border-rose-700 bg-rose-900/40 px-4 py-3 text-sm text-rose-200">
          {error}
        </div>
      )}

      {!trace && !error && (
        <div className="rounded-lg border border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
          Loading trace…
        </div>
      )}

      {trace && trace.status === "passed" && tests.length === 0 && (
        // Explain WHY a "passed" trace can be empty + give the user 2-3
        // common root causes to check, instead of just leaving them with
        // a green checkmark that hides the fact that nothing actually ran.
        <div className="mb-4 rounded-md border border-amber-700 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">
          <div className="font-medium">Run exited cleanly but recorded zero tests.</div>
          <div className="mt-1 text-xs text-amber-200/80">
            That usually means one of: (a) Maven Surefire / pytest didn't
            discover any test class — check the file naming
            (<code className="rounded bg-amber-900/30 px-1">*Test.java</code>,{" "}
            <code className="rounded bg-amber-900/30 px-1">test_*.py</code>);
            (b) the runner exited before reaching the test phase (e.g. a
            <code className="rounded bg-amber-900/30 px-1"> pre-integration-test</code> step bailed silently);
            or (c) all discovered tests were skipped via{" "}
            <code className="rounded bg-amber-900/30 px-1">@Disabled</code> /
            <code className="rounded bg-amber-900/30 px-1">@pytest.mark.skip</code>.
            Stdout/stderr at the bottom of this page usually has the
            specifics — search for{" "}
            <code className="rounded bg-amber-900/30 px-1">Tests run:</code> or{" "}
            <code className="rounded bg-amber-900/30 px-1">collected 0 items</code>.
          </div>
        </div>
      )}

      {trace && (
        <>
          <nav className="mb-4 flex flex-wrap gap-1 border-b border-slate-800">
            <TabButton tab="timeline" current={tab} onClick={setTab}>
              Timeline{" "}
              <span className="text-xs text-slate-500">({tests.length})</span>
            </TabButton>
            <TabButton tab="screenshots" current={tab} onClick={setTab}>
              Screenshots{" "}
              <span className="text-xs text-slate-500">({screenshots.length})</span>
            </TabButton>
            <TabButton tab="dom" current={tab} onClick={setTab}>
              DOM{" "}
              <span className="text-xs text-slate-500">({domSnapshots.length})</span>
            </TabButton>
            <TabButton tab="network" current={tab} onClick={setTab}>
              Network{" "}
              <span className="text-xs text-slate-500">({networkEvents.length})</span>
            </TabButton>
            <TabButton tab="triage" current={tab} onClick={setTab}>
              Triage{" "}
              <span className="text-xs text-slate-500">({triages.length})</span>
            </TabButton>
          </nav>

          {tab === "timeline" && <TimelinePanel tests={tests} />}
          {tab === "screenshots" && (
            <ScreenshotsPanel shots={screenshots} tests={tests} />
          )}
          {tab === "dom" && (
            <DomPanel snapshots={domSnapshots} tests={tests} />
          )}
          {tab === "network" && <NetworkPanel events={networkEvents} />}
          {tab === "triage" && <TriagePanel triages={triages} />}

          <LogsSection stdout={stdoutInline} stderr={stderrInline} />
        </>
      )}
    </div>
  );
}

function TabButton({
  tab,
  current,
  onClick,
  children,
}: {
  tab: Tab;
  current: Tab;
  onClick: (t: Tab) => void;
  children: React.ReactNode;
}): JSX.Element {
  const active = tab === current;
  return (
    <button
      type="button"
      onClick={() => onClick(tab)}
      className={`-mb-px rounded-t-md border border-b-0 px-3 py-1.5 text-sm ${
        active
          ? "border-slate-800 bg-slate-900 text-slate-100"
          : "border-transparent text-slate-400 hover:text-slate-200"
      }`}
    >
      {children}
    </button>
  );
}

// ───────── Timeline ─────────

function TimelinePanel({ tests }: { tests: ViewTest[] }): JSX.Element {
  if (tests.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center text-slate-400">
        No tests recorded in this trace.
      </div>
    );
  }

  // The timeline uses a horizontal proportional bar per test. Slowest test
  // spans full-width; shorter tests get a width proportional to that max.
  // This is a pragmatic stand-in for a real Gantt — without per-step
  // start timestamps (not captured in v1), absolute positioning would lie.
  const maxMs = tests.reduce((m, t) => Math.max(m, t.durationMs), 1);

  return (
    <div className="rounded-lg border border-slate-800 bg-slate-950/40">
      <ul className="divide-y divide-slate-800">
        {tests.map((t, i) => (
          <li key={`${t.className ?? ""}::${t.name}::${i}`} className="px-4 py-3">
            <div className="flex items-center gap-3">
              <StatusDot status={t.status} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm text-slate-100">
                    {t.name}
                  </span>
                  {t.className && (
                    <span className="text-xs text-slate-500">
                      {t.className}
                    </span>
                  )}
                </div>
                <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-900">
                  <div
                    className={`h-full ${barColor(t.status)}`}
                    style={{ width: `${Math.max(4, (t.durationMs / maxMs) * 100)}%` }}
                  />
                </div>
              </div>
              <div className="shrink-0 tabular-nums text-xs text-slate-400">
                {formatDuration(t.durationMs)}
              </div>
            </div>
            {(t.errorMessage || t.errorStack) && (
              <details className="mt-2 ml-7">
                <summary className="cursor-pointer text-xs text-rose-300 hover:text-rose-200">
                  {t.errorMessage
                    ? `Error: ${truncate(t.errorMessage, 140)}`
                    : "Show error stack"}
                </summary>
                {t.errorStack && (
                  <pre className="mt-2 max-h-64 overflow-auto rounded border border-slate-800 bg-slate-950 p-2 text-xs text-rose-200">
                    {t.errorStack}
                  </pre>
                )}
              </details>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function StatusDot({ status }: { status: string }): JSX.Element {
  const cls =
    status === "passed"
      ? "bg-emerald-400"
      : status === "failed"
        ? "bg-rose-400"
        : status === "errored"
          ? "bg-amber-400"
          : status === "skipped"
            ? "bg-slate-500"
            : "bg-slate-600";
  return <span className={`inline-block h-2.5 w-2.5 rounded-full ${cls}`} />;
}

function barColor(status: string): string {
  if (status === "passed") return "bg-emerald-500/70";
  if (status === "failed") return "bg-rose-500/70";
  if (status === "errored") return "bg-amber-500/70";
  if (status === "skipped") return "bg-slate-600/60";
  return "bg-slate-500/60";
}

function truncate(s: string, n: number): string {
  return s.length > n ? s.slice(0, n - 1) + "…" : s;
}

// ───────── Screenshots ─────────

function ScreenshotsPanel({
  shots,
  tests,
}: {
  shots: ViewArtifact[];
  tests: ViewTest[];
}): JSX.Element {
  const [active, setActive] = useState(0);
  const current = shots[active] ?? null;

  if (shots.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center">
        <p className="text-slate-300">No screenshots in this trace.</p>
        <p className="mt-1 text-sm text-slate-500">
          Enable step-level screenshots in your emitted test (Java:{" "}
          <code className="rounded bg-slate-800 px-1">
            UaipTrace.screenshot("step-id")
          </code>
          ) to populate this panel on the next run.
        </p>
      </div>
    );
  }

  // Group shots by testName so the sidebar can show a collapsed list per
  // test. Screenshots without a testName land in "(unscoped)".
  const groups = new Map<string, ViewArtifact[]>();
  for (const s of shots) {
    const key = s.testName ?? "(unscoped)";
    const g = groups.get(key) ?? [];
    g.push(s);
    groups.set(key, g);
  }

  // Build a flat ordered index matching the sidebar display order, so
  // the left-column "next/prev" chevrons work correctly.
  const flatOrder: ViewArtifact[] = [];
  const orderedKeys = [...groups.keys()];
  // Put tests in the order they appear in `tests`, then any unmatched
  // group keys at the end.
  const testOrder = tests.map((t) => t.name);
  orderedKeys.sort((a, b) => {
    const ai = testOrder.indexOf(a);
    const bi = testOrder.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  for (const k of orderedKeys) {
    for (const s of groups.get(k) ?? []) flatOrder.push(s);
  }

  const activeIdx = current ? flatOrder.indexOf(current) : -1;

  return (
    <div className="grid gap-4 md:grid-cols-[260px_1fr]">
      <aside className="rounded-lg border border-slate-800 bg-slate-950/40 p-2">
        <ul className="max-h-[60vh] overflow-y-auto text-sm">
          {orderedKeys.map((k) => (
            <li key={k} className="mb-2">
              <div className="px-2 pb-1 text-xs uppercase tracking-wider text-slate-500">
                {k}
              </div>
              <ul className="space-y-0.5">
                {(groups.get(k) ?? []).map((s, j) => {
                  const idx = flatOrder.indexOf(s);
                  const sel = idx === activeIdx;
                  return (
                    <li key={`${k}-${j}`}>
                      <button
                        type="button"
                        onClick={() => setActive(idx)}
                        className={`w-full truncate rounded px-2 py-1 text-left text-xs font-mono hover:bg-slate-900 ${
                          sel ? "bg-slate-900 text-indigo-300" : "text-slate-300"
                        }`}
                      >
                        {s.stepId ?? basename(s.path)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      </aside>

      <section className="min-w-0 rounded-lg border border-slate-800 bg-slate-950/40 p-3">
        {current ? (
          <>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="font-mono text-slate-400 truncate">{current.path}</div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={activeIdx <= 0}
                  onClick={() => setActive(Math.max(0, activeIdx - 1))}
                  className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 disabled:opacity-40"
                >
                  ← Prev
                </button>
                <span className="text-slate-500">
                  {activeIdx + 1} / {flatOrder.length}
                </span>
                <button
                  type="button"
                  disabled={activeIdx >= flatOrder.length - 1}
                  onClick={() =>
                    setActive(Math.min(flatOrder.length - 1, activeIdx + 1))
                  }
                  className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            </div>
            {/*
              For v0 the screenshot file isn't served by the API — the manifest
              references a relative path the CLI wrote to disk. Once we add a
              GET /api/runs/:id/trace/artifact route (v1) this <img> will point
              at that. For now we render a placeholder frame + metadata so the
              UX is correct even without the bytes.
            */}
            <div className="flex items-center justify-center rounded border border-dashed border-slate-700 bg-slate-900/60 p-6 text-slate-500">
              <div className="text-center">
                <div className="text-sm">
                  {current.width && current.height
                    ? `${current.width} × ${current.height}`
                    : "screenshot"}
                </div>
                <div className="mt-1 font-mono text-[11px] text-slate-600">
                  {current.path}
                </div>
                {current.ts && (
                  <div className="mt-2 text-[11px] text-slate-600">
                    captured {formatRelativeTime(current.ts)}
                  </div>
                )}
                <div className="mt-3 text-[11px] text-slate-500">
                  Artifact streaming will land with the GET{" "}
                  <code className="rounded bg-slate-800 px-1 py-0.5">
                    /api/runs/:id/trace/artifact
                  </code>{" "}
                  route.
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="p-6 text-center text-slate-500">
            Select a screenshot from the left to preview it.
          </div>
        )}
      </section>
    </div>
  );
}

function basename(p: string): string {
  const i = p.lastIndexOf("/");
  return i === -1 ? p : p.slice(i + 1);
}

// ───────── DOM snapshot panel (task #108) ─────────

function DomPanel({
  snapshots,
  tests,
}: {
  snapshots: ViewArtifact[];
  tests: ViewTest[];
}): JSX.Element {
  const [active, setActive] = useState(0);
  const [mode, setMode] = useState<"html" | "diff">("html");

  if (snapshots.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center">
        <p className="text-slate-300">No DOM snapshots in this trace.</p>
        <p className="mt-1 text-sm text-slate-500">
          Call{" "}
          <code className="rounded bg-slate-800 px-1">
            UaipTrace.domSnapshot("step-id")
          </code>{" "}
          from your emitted test to capture the current page's HTML into the
          trace. The snapshot will show up here alongside the screenshots
          gallery.
        </p>
      </div>
    );
  }

  // Group + flat-order the same way the screenshots panel does, so
  // chronological navigation across tests works as expected.
  const groups = new Map<string, ViewArtifact[]>();
  for (const s of snapshots) {
    const key = s.testName ?? "(unscoped)";
    const g = groups.get(key) ?? [];
    g.push(s);
    groups.set(key, g);
  }
  const testOrder = tests.map((t) => t.name);
  const orderedKeys = [...groups.keys()].sort((a, b) => {
    const ai = testOrder.indexOf(a);
    const bi = testOrder.indexOf(b);
    if (ai === -1 && bi === -1) return a.localeCompare(b);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
  const flat: ViewArtifact[] = [];
  for (const k of orderedKeys) for (const s of groups.get(k) ?? []) flat.push(s);

  const current = flat[active] ?? null;

  // The previous snapshot *within the same test group* is what "diff" is
  // compared against. If current is the first in its group, diff is a no-op.
  let prev: ViewArtifact | null = null;
  if (current) {
    const key = current.testName ?? "(unscoped)";
    const group = groups.get(key) ?? [];
    const i = group.indexOf(current);
    if (i > 0) prev = group[i - 1] ?? null;
  }

  const currentHtml = current?.htmlInline ?? "";
  const prevHtml = prev?.htmlInline ?? "";

  return (
    <div className="grid gap-4 md:grid-cols-[260px_1fr]">
      <aside className="rounded-lg border border-slate-800 bg-slate-950/40 p-2">
        <ul className="max-h-[60vh] overflow-y-auto text-sm">
          {orderedKeys.map((k) => (
            <li key={k} className="mb-2">
              <div className="px-2 pb-1 text-xs uppercase tracking-wider text-slate-500">
                {k}
              </div>
              <ul className="space-y-0.5">
                {(groups.get(k) ?? []).map((s, j) => {
                  const idx = flat.indexOf(s);
                  const sel = idx === active;
                  return (
                    <li key={`${k}-${j}`}>
                      <button
                        type="button"
                        onClick={() => setActive(idx)}
                        className={`w-full truncate rounded px-2 py-1 text-left text-xs font-mono hover:bg-slate-900 ${
                          sel ? "bg-slate-900 text-indigo-300" : "text-slate-300"
                        }`}
                      >
                        {s.stepId ?? basename(s.path)}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      </aside>

      <section className="min-w-0 rounded-lg border border-slate-800 bg-slate-950/40 p-3">
        {current ? (
          <>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="truncate font-mono text-slate-400">{current.path}</div>
              <div className="flex items-center gap-2">
                <div className="flex items-center rounded border border-slate-700 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setMode("html")}
                    className={`px-2 py-0.5 ${
                      mode === "html"
                        ? "bg-slate-900 text-indigo-300"
                        : "text-slate-400 hover:text-slate-200"
                    }`}
                  >
                    HTML
                  </button>
                  <button
                    type="button"
                    onClick={() => setMode("diff")}
                    disabled={!prev}
                    className={`px-2 py-0.5 ${
                      mode === "diff"
                        ? "bg-slate-900 text-indigo-300"
                        : "text-slate-400 hover:text-slate-200"
                    } disabled:opacity-40 disabled:cursor-not-allowed`}
                    title={prev ? "Diff against previous snapshot" : "No previous snapshot in this test"}
                  >
                    Diff
                  </button>
                </div>
                <button
                  type="button"
                  disabled={active <= 0}
                  onClick={() => setActive(Math.max(0, active - 1))}
                  className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 disabled:opacity-40"
                >
                  ← Prev
                </button>
                <span className="text-slate-500">
                  {active + 1} / {flat.length}
                </span>
                <button
                  type="button"
                  disabled={active >= flat.length - 1}
                  onClick={() => setActive(Math.min(flat.length - 1, active + 1))}
                  className="rounded border border-slate-700 px-2 py-0.5 text-slate-300 disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            </div>

            {mode === "html" ? (
              <HtmlView html={currentHtml} meta={current} />
            ) : (
              <DiffView
                prevHtml={prevHtml}
                currentHtml={currentHtml}
                hasPrev={prev !== null}
              />
            )}
          </>
        ) : (
          <div className="p-6 text-center text-slate-500">
            Select a DOM snapshot from the left to inspect it.
          </div>
        )}
      </section>
    </div>
  );
}

function HtmlView({
  html,
  meta,
}: {
  html: string;
  meta: ViewArtifact;
}): JSX.Element {
  if (!html) {
    return (
      <div className="rounded border border-dashed border-slate-700 bg-slate-900/60 p-6 text-sm text-slate-500">
        <p>
          This DOM snapshot is stored on disk at{" "}
          <code className="rounded bg-slate-800 px-1">{meta.path}</code>.
        </p>
        <p className="mt-2 text-[11px] text-slate-500">
          Inline HTML viewing lands when the API exposes GET{" "}
          <code className="rounded bg-slate-800 px-1 py-0.5">
            /api/runs/:id/trace/artifact
          </code>
          . The CLI can already inline small documents by adding{" "}
          <code className="rounded bg-slate-800 px-1">htmlInline</code> to the
          manifest entry.
        </p>
        {meta.bytes !== undefined && (
          <p className="mt-2 text-[11px] text-slate-600">
            {meta.bytes.toLocaleString()} bytes on disk
          </p>
        )}
      </div>
    );
  }
  return (
    <pre className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words rounded border border-slate-800 bg-slate-950 p-3 text-xs text-slate-200">
      {html}
    </pre>
  );
}

/**
 * Line-by-line diff. We keep it deliberately simple: a Myers-style LCS
 * is overkill for a v0 that's usually looking at a DOM delta between
 * two very similar snapshots. Instead we compare line arrays and mark:
 *   - lines present in current but not previous → "added" (green)
 *   - lines present in previous but not current → "removed" (red)
 *   - lines present in both → shown in dim slate
 *
 * Order follows the *current* snapshot with removed lines interleaved
 * at the position their neighbour occupies in the previous version.
 * Good enough to tell "a button appeared/disappeared" at a glance
 * without dragging in a diff lib.
 */
function DiffView({
  prevHtml,
  currentHtml,
  hasPrev,
}: {
  prevHtml: string;
  currentHtml: string;
  hasPrev: boolean;
}): JSX.Element {
  if (!hasPrev) {
    return (
      <div className="rounded border border-dashed border-slate-700 bg-slate-900/60 p-6 text-center text-sm text-slate-500">
        This is the first DOM snapshot in its test — nothing to diff against.
      </div>
    );
  }
  if (!currentHtml && !prevHtml) {
    return (
      <div className="rounded border border-dashed border-slate-700 bg-slate-900/60 p-6 text-sm text-slate-500">
        Neither snapshot has inline HTML yet — wire up{" "}
        <code className="rounded bg-slate-800 px-1">htmlInline</code> in the
        writer to enable the diff viewer.
      </div>
    );
  }

  const prevLines = prevHtml.split("\n");
  const curLines = currentHtml.split("\n");
  const prevSet = new Set(prevLines);
  const curSet = new Set(curLines);

  const rows: Array<{ kind: "same" | "added" | "removed"; text: string }> = [];
  // Interleave: walk `curLines` in order; for each "same" line, before
  // emitting it flush any `prevLines` that sat before it but don't
  // appear in `curSet` (those are "removed"). Cheap and close enough.
  let pi = 0;
  for (const line of curLines) {
    if (prevSet.has(line)) {
      // Drain prev-only lines up to this common line.
      while (pi < prevLines.length && prevLines[pi] !== line) {
        const pl = prevLines[pi]!;
        if (!curSet.has(pl)) rows.push({ kind: "removed", text: pl });
        pi++;
      }
      if (pi < prevLines.length) pi++; // skip the matched common line
      rows.push({ kind: "same", text: line });
    } else {
      rows.push({ kind: "added", text: line });
    }
  }
  while (pi < prevLines.length) {
    const pl = prevLines[pi]!;
    if (!curSet.has(pl)) rows.push({ kind: "removed", text: pl });
    pi++;
  }

  const added = rows.filter((r) => r.kind === "added").length;
  const removed = rows.filter((r) => r.kind === "removed").length;

  return (
    <div>
      <div className="mb-2 text-xs text-slate-500">
        <span className="text-emerald-400">+{added}</span>{" "}
        <span className="text-rose-400">-{removed}</span> lines
      </div>
      <pre className="max-h-[60vh] overflow-auto rounded border border-slate-800 bg-slate-950 text-xs">
        {rows.map((r, i) => (
          <div
            key={i}
            className={
              r.kind === "added"
                ? "bg-emerald-950/40 text-emerald-200 whitespace-pre-wrap break-words px-2"
                : r.kind === "removed"
                  ? "bg-rose-950/40 text-rose-200 whitespace-pre-wrap break-words px-2"
                  : "text-slate-400 whitespace-pre-wrap break-words px-2"
            }
          >
            <span className="mr-2 select-none text-slate-600">
              {r.kind === "added" ? "+" : r.kind === "removed" ? "-" : " "}
            </span>
            {r.text}
          </div>
        ))}
      </pre>
    </div>
  );
}

// ───────── Network panel (task #109) ─────────

function NetworkPanel({
  events,
}: {
  events: ViewNetworkEvent[];
}): JSX.Element {
  const [filter, setFilter] = useState<"all" | "2xx" | "4xx" | "5xx" | "fail">(
    "all",
  );
  const [q, setQ] = useState("");

  if (events.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center">
        <p className="text-slate-300">No network requests recorded.</p>
        <p className="mt-1 text-sm text-slate-500">
          Tracing emitted tests produces a <code className="rounded bg-slate-800 px-1">networkRequests</code>{" "}
          array on each trace manifest. For Selenium tests this requires the
          Chrome DevTools Protocol shim (coming with the self-heal epic);
          Playwright captures these natively via <code className="rounded bg-slate-800 px-1">page.on("request")</code>.
        </p>
      </div>
    );
  }

  const filtered = events.filter((e) => {
    if (filter === "2xx" && !(e.status >= 200 && e.status < 300)) return false;
    if (filter === "4xx" && !(e.status >= 400 && e.status < 500)) return false;
    if (filter === "5xx" && !(e.status >= 500 && e.status < 600)) return false;
    if (filter === "fail" && e.status !== -1 && !(e.status >= 400)) return false;
    if (q.trim().length > 0) {
      const needle = q.trim().toLowerCase();
      if (
        !e.url.toLowerCase().includes(needle) &&
        !e.method.toLowerCase().includes(needle)
      ) {
        return false;
      }
    }
    return true;
  });

  const maxMs = filtered.reduce((m, e) => Math.max(m, e.durationMs), 1);
  const totalBytes = filtered.reduce(
    (sum, e) => sum + (e.responseBytes ?? 0),
    0,
  );

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-slate-800 bg-slate-900/40 p-3">
        <div className="flex items-center rounded border border-slate-700 overflow-hidden text-xs">
          {(["all", "2xx", "4xx", "5xx", "fail"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setFilter(k)}
              className={`px-2 py-1 ${
                filter === k
                  ? "bg-slate-900 text-indigo-300"
                  : "text-slate-400 hover:text-slate-200"
              }`}
            >
              {k}
            </button>
          ))}
        </div>
        <label className="ml-auto block text-xs text-slate-400">
          <span className="mr-2 uppercase tracking-wider">Search</span>
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="URL or method…"
            className="w-56 rounded-md border border-slate-700 bg-slate-950 px-2 py-1 text-sm text-slate-100"
          />
        </label>
        <div className="text-xs text-slate-500 tabular-nums">
          {filtered.length} / {events.length} · {formatBytes(totalBytes)} downloaded
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center text-sm text-slate-400">
          No requests match these filters.
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-800">
          <table className="w-full text-sm">
            <thead className="bg-slate-900/80 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Method</th>
                <th className="px-3 py-2 font-medium">URL</th>
                <th className="px-3 py-2 font-medium">Size</th>
                <th className="px-3 py-2 font-medium w-72">Time</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800 bg-slate-950/40">
              {filtered.map((e, i) => (
                <tr key={i} className="hover:bg-slate-900/60">
                  <td className="px-3 py-2 tabular-nums">
                    <NetStatus status={e.status} />
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-slate-300">
                    {e.method}
                  </td>
                  <td className="px-3 py-2 max-w-[28rem]">
                    <div
                      className="truncate font-mono text-xs text-slate-200"
                      title={e.url}
                    >
                      {e.url}
                    </div>
                    {e.fromTest && (
                      <div className="truncate text-[11px] text-slate-500">
                        {e.fromTest}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 tabular-nums text-xs text-slate-400">
                    {formatBytes(e.responseBytes ?? 0)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 rounded-full bg-slate-900 overflow-hidden">
                        <div
                          className={`h-full ${netBarColor(e.status)}`}
                          style={{
                            width: `${Math.max(4, (e.durationMs / maxMs) * 100)}%`,
                          }}
                        />
                      </div>
                      <span className="w-14 shrink-0 text-right tabular-nums text-xs text-slate-400">
                        {formatDuration(e.durationMs)}
                      </span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function NetStatus({ status }: { status: number }): JSX.Element {
  if (status === -1) {
    return (
      <span className="rounded bg-slate-800 px-1.5 py-0.5 text-xs text-slate-400">
        —
      </span>
    );
  }
  let cls = "bg-slate-800 text-slate-300";
  if (status >= 200 && status < 300) cls = "bg-emerald-900/50 text-emerald-300";
  else if (status >= 300 && status < 400) cls = "bg-sky-900/50 text-sky-300";
  else if (status >= 400 && status < 500) cls = "bg-amber-900/50 text-amber-300";
  else if (status >= 500) cls = "bg-rose-900/50 text-rose-300";
  return (
    <span className={`rounded px-1.5 py-0.5 text-xs tabular-nums ${cls}`}>
      {status}
    </span>
  );
}

function netBarColor(status: number): string {
  if (status === -1) return "bg-slate-600/70";
  if (status >= 500) return "bg-rose-500/70";
  if (status >= 400) return "bg-amber-500/70";
  if (status >= 300) return "bg-sky-500/70";
  return "bg-emerald-500/70";
}

function formatBytes(b: number): string {
  if (b <= 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

// ───────── Logs ─────────

function LogsSection({
  stdout,
  stderr,
}: {
  stdout: string;
  stderr: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const hasLogs = stdout.length > 0 || stderr.length > 0;
  if (!hasLogs) return <></>;
  return (
    <section className="mt-6">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full rounded-md border border-slate-800 bg-slate-900/60 px-3 py-2 text-left text-sm text-slate-300 hover:bg-slate-900"
      >
        <span className="mr-2">{open ? "▼" : "▶"}</span>Logs (
        {stdout ? "stdout" : ""}
        {stdout && stderr ? " + " : ""}
        {stderr ? "stderr" : ""})
      </button>
      {open && (
        <div className="mt-2 grid gap-3 md:grid-cols-2">
          {stdout && (
            <LogBlock title="stdout" body={stdout} tone="slate" />
          )}
          {stderr && <LogBlock title="stderr" body={stderr} tone="rose" />}
        </div>
      )}
    </section>
  );
}

function LogBlock({
  title,
  body,
  tone,
}: {
  title: string;
  body: string;
  tone: "slate" | "rose";
}): JSX.Element {
  const border =
    tone === "rose" ? "border-rose-900/60" : "border-slate-800";
  const text = tone === "rose" ? "text-rose-200" : "text-slate-200";
  return (
    <div className={`rounded-lg border ${border} bg-slate-950/60 p-2`}>
      <div className="mb-1 px-1 text-xs uppercase tracking-wider text-slate-500">
        {title}
      </div>
      <pre
        className={`max-h-80 overflow-auto whitespace-pre-wrap break-words rounded border border-slate-900 bg-slate-950 p-2 text-xs ${text}`}
      >
        {body}
      </pre>
    </div>
  );
}

// ───────── Triage ─────────

/**
 * TriagePanel — Plan §16.6 "LLM-Assisted Runs" surface.
 *
 * For each failing test that went through the triage pipeline we render:
 *   - header row: test name + confidence badge + provider/model footnote
 *   - category pill + proposed-action chip
 *   - root-cause hypothesis (one sentence, large typography)
 *   - evidence bullets
 *   - when `selfHealSuggestion` is present, a panel showing old → new
 *     selector with the fallback bundle, plus a hint that the existing
 *     Send-as-PR flow on the Tests tab can accept this as a repair.
 *
 * Intentionally *read-only* in v1. The "apply heal" action routes through
 * the already-shipped heals CRUD flow on a follow-up sprint; that keeps
 * this tab deployable the day the CLI starts writing triage.json without
 * also needing API changes.
 */
function TriagePanel({ triages }: { triages: ViewTriage[] }): JSX.Element {
  if (triages.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-800 bg-slate-900/40 p-8 text-center">
        <p className="text-slate-300">No triage results in this trace.</p>
        <p className="mt-1 text-sm text-slate-500">
          Re-run with{" "}
          <code className="rounded bg-slate-800 px-1">
            uaip run &lt;folder&gt; --trace out --triage
          </code>{" "}
          to have UAIP's local LLM diagnose failing tests.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {triages.map((t, i) => (
        <TriageCard key={`${t.failureSignature}::${i}`} triage={t} />
      ))}
    </div>
  );
}

function TriageCard({ triage }: { triage: ViewTriage }): JSX.Element {
  const confPct = Math.round(triage.confidence * 100);
  return (
    <article className="rounded-lg border border-slate-800 bg-slate-950/50">
      <header className="flex flex-wrap items-center gap-2 border-b border-slate-800 px-4 py-3">
        <span className="font-mono text-sm text-slate-100">{triage.testName}</span>
        <ConfidenceBadge confidence={triage.confidence} />
        <CategoryPill category={triage.category} />
        <ActionChip action={triage.proposedAction} />
        <span className="ml-auto text-xs text-slate-500">
          {triage.provider}
          {triage.model ? ` · ${triage.model}` : ""}
          {triage.durationMs ? ` · ${formatDuration(triage.durationMs)}` : ""}
        </span>
      </header>
      <div className="px-4 py-3">
        <p className="text-sm text-slate-200">{triage.rootCauseHypothesis}</p>
        {triage.evidence.length > 0 && (
          <ul className="mt-3 space-y-1 pl-4 text-sm text-slate-400">
            {triage.evidence.map((ev, i) => (
              <li
                key={i}
                className="relative before:absolute before:-left-3 before:top-2 before:h-1 before:w-1 before:rounded-full before:bg-slate-600"
              >
                {ev}
              </li>
            ))}
          </ul>
        )}
        {triage.selfHealSuggestion && (
          <HealProposal suggestion={triage.selfHealSuggestion} />
        )}
        {!triage.selfHealSuggestion && triage.category === "selector_drift" && (
          <p className="mt-3 text-xs text-amber-300">
            Classified as selector drift, but the LLM didn't propose a concrete
            repair — escalated to "investigate".
          </p>
        )}
        <p className="mt-3 text-[11px] text-slate-600">
          signature <code className="font-mono">{triage.failureSignature}</code>
          {triage.completedAt ? ` · ${formatRelativeTime(triage.completedAt)}` : ""} · {confPct}%
        </p>
      </div>
    </article>
  );
}

function ConfidenceBadge({ confidence }: { confidence: number }): JSX.Element {
  const pct = Math.round(confidence * 100);
  // Thresholds match §16.6: 0.90 is the default auto-apply ceiling.
  const color =
    confidence >= 0.9
      ? "border-emerald-600 bg-emerald-900/40 text-emerald-200"
      : confidence >= 0.6
        ? "border-amber-600 bg-amber-900/30 text-amber-200"
        : "border-rose-600 bg-rose-900/30 text-rose-200";
  return (
    <span
      className={`rounded-full border px-2 py-0.5 text-[10px] font-mono uppercase tracking-wide ${color}`}
    >
      {pct}% conf
    </span>
  );
}

function CategoryPill({ category }: { category: string }): JSX.Element {
  const label = category.replace(/_/g, " ");
  const color =
    category === "selector_drift"
      ? "border-sky-700 bg-sky-900/40 text-sky-200"
      : category === "app_bug"
        ? "border-rose-700 bg-rose-900/40 text-rose-200"
        : category === "timing"
          ? "border-amber-700 bg-amber-900/40 text-amber-200"
          : category === "flaky"
            ? "border-violet-700 bg-violet-900/40 text-violet-200"
            : category === "environment"
              ? "border-teal-700 bg-teal-900/40 text-teal-200"
              : "border-slate-700 bg-slate-900/40 text-slate-300";
  return (
    <span className={`rounded border px-2 py-0.5 text-[11px] ${color}`}>{label}</span>
  );
}

function ActionChip({ action }: { action: string }): JSX.Element {
  // proposed_action maps to a suggested next step; only repair_selector
  // has a direct UI action today (the heal panel below), the rest are
  // informational.
  const label = action.replace(/_/g, " ");
  return (
    <span className="rounded-full border border-slate-700 bg-slate-900/40 px-2 py-0.5 text-[11px] text-slate-300">
      → {label}
    </span>
  );
}

function HealProposal({
  suggestion,
}: {
  suggestion: NonNullable<ViewTriage["selfHealSuggestion"]>;
}): JSX.Element {
  return (
    <div className="mt-3 rounded-md border border-slate-800 bg-slate-900/70 p-3">
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">
        Proposed selector repair
      </div>
      <dl className="mt-2 space-y-2 text-xs font-mono">
        <div>
          <dt className="text-rose-400">− old</dt>
          <dd className="text-slate-300 pl-3 break-all">{suggestion.oldSelector}</dd>
        </div>
        <div>
          <dt className="text-emerald-400">+ new (primary)</dt>
          <dd className="text-slate-100 pl-3 break-all">
            {suggestion.newSelectorBundle[0]}
          </dd>
        </div>
        {suggestion.newSelectorBundle.slice(1).map((fallback, i) => (
          <div key={i}>
            <dt className="text-slate-500">+ fallback {i + 1}</dt>
            <dd className="text-slate-400 pl-3 break-all">{fallback}</dd>
          </div>
        ))}
      </dl>
      <p className="mt-2 text-xs text-slate-400">{suggestion.rationale}</p>
      <p className="mt-2 text-[11px] text-slate-500">
        Confidence {Math.round(suggestion.confidence * 100)}%. Apply via the
        heals review flow — <em>Send as PR</em> on the Tests tab packages this
        change into a branch for review.
      </p>
    </div>
  );
}

// ───────── Share dialog ─────────

/**
 * Modal for minting / rotating / revoking a shareable trace URL.
 *
 * Three states:
 *   1. Not yet shared — TTL picker + "Create link" CTA.
 *   2. Just minted (or rotation just happened) — show URL + Copy + Rotate +
 *      Revoke. The URL is ALSO copy-able from re-opening this dialog later
 *      because we re-fetch on every "Create link" click… well, until we
 *      add a separate "fetch existing token" route. For v0 the dialog
 *      assumes you're either creating fresh or rotating.
 *   3. Already-shared on entry — explain that we don't surface the existing
 *      token (security: avoid having tokens loiter in the DOM), but offer
 *      Rotate (which re-mints and shows the new URL) and Revoke.
 *
 * TTL options: never / 1h / 24h / 7d / 30d. Beyond 30d feels less like
 * "share with a teammate" and more like "host an artifact".
 */
function ShareDialog(props: {
  runId: string;
  alreadyShared: boolean;
  onClose: () => void;
  onChange: (isShared: boolean) => void;
}): JSX.Element {
  const { runId, alreadyShared, onClose, onChange } = props;
  const [ttlHours, setTtlHours] = useState<number>(0); // 0 = never expires
  const [share, setShare] = useState<ShareInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function mint(): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const result = await createTraceShare(runId, ttlHours > 0 ? { ttlHours } : {});
      setShare(result);
      onChange(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function revoke(): Promise<void> {
    if (!confirm("Revoke this share link? The URL will stop working.")) return;
    setBusy(true);
    setErr(null);
    try {
      await deleteTraceShare(runId);
      setShare(null);
      onChange(false);
      onClose();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function copy(): Promise<void> {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share.shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard API may be unavailable on http or in a sandbox; fall
      // back to a select-all hint via prompt() so the user can copy manually.
      prompt("Copy share URL:", share.shareUrl);
    }
  }

  // Backdrop click closes the dialog when not in the middle of a request.
  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-40 flex items-center justify-center bg-slate-950/80 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-lg border border-slate-700 bg-slate-900 p-5 shadow-xl">
        <div className="mb-3 flex items-start justify-between">
          <h2 className="text-base font-semibold text-slate-100">
            Share this trace
          </h2>
          <button
            type="button"
            onClick={() => !busy && onClose()}
            className="text-slate-500 hover:text-slate-200"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {!share && alreadyShared && (
          <div className="mb-3 rounded-md border border-amber-700 bg-amber-900/30 p-3 text-xs text-amber-100">
            This trace already has an active share link. For your security we
            don't redisplay the existing URL — generate a new one to copy it
            again. Doing so revokes the previous URL.
          </div>
        )}

        {!share && (
          <>
            <p className="text-sm text-slate-300">
              Anyone with the link can view the manifest, screenshots, and logs
              for this trace. They won't be able to upload, modify, or run
              anything.
            </p>
            <label className="mt-4 block text-xs uppercase tracking-wide text-slate-400">
              Link expires after
            </label>
            <select
              value={ttlHours}
              onChange={(e) => setTtlHours(Number(e.target.value))}
              className="mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 text-sm text-slate-100"
              disabled={busy}
            >
              <option value={0}>Never (until you revoke)</option>
              <option value={1}>1 hour</option>
              <option value={24}>24 hours</option>
              <option value={24 * 7}>7 days</option>
              <option value={24 * 30}>30 days</option>
            </select>

            {err && (
              <div className="mt-3 rounded-md border border-rose-700 bg-rose-900/40 px-3 py-2 text-xs text-rose-200">
                {err}
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              {alreadyShared && (
                <button
                  type="button"
                  onClick={revoke}
                  disabled={busy}
                  className="rounded-md border border-rose-700 bg-rose-900/30 px-3 py-1.5 text-sm text-rose-200 hover:bg-rose-900/60 disabled:opacity-60"
                >
                  Revoke existing
                </button>
              )}
              <button
                type="button"
                onClick={mint}
                disabled={busy}
                className="rounded-md border border-emerald-700 bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-600 disabled:opacity-60"
              >
                {busy ? "Working…" : alreadyShared ? "Rotate link" : "Create link"}
              </button>
            </div>
          </>
        )}

        {share && (
          <>
            <p className="text-sm text-slate-300">
              Anyone with this link can view the trace.
              {share.shareExpiresAt
                ? ` It expires ${formatRelativeTime(share.shareExpiresAt)}.`
                : " It does not expire until you revoke it."}
            </p>
            <div className="mt-3 flex gap-2">
              <input
                readOnly
                value={share.shareUrl}
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 rounded-md border border-slate-700 bg-slate-950 px-3 py-1.5 font-mono text-xs text-slate-100"
              />
              <button
                type="button"
                onClick={copy}
                className="rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-700"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>

            {err && (
              <div className="mt-3 rounded-md border border-rose-700 bg-rose-900/40 px-3 py-2 text-xs text-rose-200">
                {err}
              </div>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={revoke}
                disabled={busy}
                className="rounded-md border border-rose-700 bg-rose-900/30 px-3 py-1.5 text-sm text-rose-200 hover:bg-rose-900/60 disabled:opacity-60"
              >
                Revoke
              </button>
              <button
                type="button"
                onClick={onClose}
                className="rounded-md border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-700"
              >
                Done
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
